import React, { useState, useEffect, useCallback, useMemo, useContext, useRef, forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import clsx from "clsx";
import Accordian from "../../../../../components/Accordian";
import Spinner from "../../../../../components/Spinner";
import Checkbox from "../../../../../components/Checkbox";
import Button from "../../../../../components/Button";
import Toast from "../../../../../components/Toast";
import TableBox from "../../../../../components/TableBox";
import Modal from "../../../../../components/Modal";
import { HeadCellType, TableContextProvider } from "../../../../../interfaces/common";
import { buildHeadCells, formatAmount, formatDollarAmount, getDisplayedMarginAmount, sortRowsByAccessor } from "../../../../../utils/methods";
import { VALIDATION_FIELD_MAPPING } from "../../../../../utils/appConstant";
import { SortOrder, RoleCodes } from "../../../../../enums";
import { AuthContext } from "../../../../../contexts/AuthContext";
import ManagerStatementService from "../../../../../services/managerStatement.service";
import { AdjustTransactionForm, TransactionListItem } from "../../../../../interfaces/managerStatement.interface";
import TransactionsRow from "./TransactionsRow";
import AdjustTransactionModalContent from "./AdjustTransactionModalContent";
import AdjustTransactionModalButtons from "./AdjustTransactionModalButtons";
import PaidToManagerCalculationModalContent from "./PaidToManagerCalculationModalContent";

interface TransactionsSectionProps {
  statementId: string;
  managerName?: string | null;
  readOnly?: boolean;
  onTotalsChange?: (paidToManager: number) => void;
  onLinked?: () => void;
}

export interface TransactionsSectionHandle {
  save: () => Promise<void>;
  isDirty: () => boolean;
}

const HEAD_CELLS = buildHeadCells([
  VALIDATION_FIELD_MAPPING.select,
  VALIDATION_FIELD_MAPPING.date,
  VALIDATION_FIELD_MAPPING.propertyName,
  VALIDATION_FIELD_MAPPING.expenseCategory,
  VALIDATION_FIELD_MAPPING.paidBy,
  VALIDATION_FIELD_MAPPING.transactionType,
  VALIDATION_FIELD_MAPPING.status,
  VALIDATION_FIELD_MAPPING.actualAmount,
  VALIDATION_FIELD_MAPPING.marginAmount,
  VALIDATION_FIELD_MAPPING.paidToManager,
  VALIDATION_FIELD_MAPPING.transactionAdjustAction,
], 'managerStatementTransactionsList');

const toSortableDate = (date: string | null): string | null => {
  if (!date) return null;
  const [month, day, year] = date.split('/');
  return `${year}-${month}-${day}`;
};

const TRANSACTIONS_SORT_ACCESSORS: Record<string, (item: TransactionListItem) => string | number | null | undefined> = {
  [VALIDATION_FIELD_MAPPING.date]: (item) => toSortableDate(item.date),
  [VALIDATION_FIELD_MAPPING.propertyName]: (item) => item.property_name,
  [VALIDATION_FIELD_MAPPING.expenseCategory]: (item) => item.expense_category_label,
  [VALIDATION_FIELD_MAPPING.paidBy]: (item) => item.paid_by_label,
  [VALIDATION_FIELD_MAPPING.transactionType]: (item) => item.transaction_type_name,
  [VALIDATION_FIELD_MAPPING.status]: (item) => item.transaction_status_label,
  [VALIDATION_FIELD_MAPPING.actualAmount]: (item) => item.actual_amount,
  [VALIDATION_FIELD_MAPPING.marginAmount]: (item) => getDisplayedMarginAmount(item),
  [VALIDATION_FIELD_MAPPING.paidToManager]: (item) => item.paid_to_manager,
};

const EMPTY_ADJUST_FORM: AdjustTransactionForm = {
  paid_to_manager_override: '',
  manager_note: '',
  admin_note: '',
  is_resolved: false,
};

const ADJUST_FIELD_NAME_MAP: Record<string, keyof AdjustTransactionForm> = {
  [VALIDATION_FIELD_MAPPING.paidToManagerOverride]: 'paid_to_manager_override',
  [VALIDATION_FIELD_MAPPING.managerNote]: 'manager_note',
  [VALIDATION_FIELD_MAPPING.adminNote]: 'admin_note',
  [VALIDATION_FIELD_MAPPING.markAsResolved]: 'is_resolved',
};

const TransactionsSection = forwardRef<TransactionsSectionHandle, TransactionsSectionProps>(({ statementId, managerName, readOnly, onTotalsChange, onLinked }, ref) => {
  const { t } = useTranslation();
  const { isSystemAdmin, loggedInUser } = useContext(AuthContext);
  const isAdminCaller = isSystemAdmin === 'true' || loggedInUser?.role_code === RoleCodes.admin;

  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<TransactionListItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [order, setOrder] = useState<SortOrder>(SortOrder.desc);
  const [orderBy, setOrderBy] = useState<string>(VALIDATION_FIELD_MAPPING.date);
  const [adjustModal, setAdjustModal] = useState<{ open: boolean; item: TransactionListItem | null }>({ open: false, item: null });
  const [calcModal, setCalcModal] = useState<{ open: boolean; item: TransactionListItem | null }>({ open: false, item: null });
  const [adjustFormData, setAdjustFormData] = useState<AdjustTransactionForm>(EMPTY_ADJUST_FORM);
  const [adjustSaving, setAdjustSaving] = useState(false);
  const [attaching, setAttaching] = useState(false);

  // Tracks the transaction IDs last confirmed saved to (or loaded from) the server, so
  // isDirty() can tell an in-progress selection change apart from the persisted state
  // without a second network round-trip.
  const savedSelectedIdsRef = useRef<number[]>([]);

  // silent=true skips the full-table spinner - used for a background refresh (e.g. after
  // saveSelection() re-fetches to pick up a newly attached row's real data) so the table
  // doesn't blank out and reappear for what is otherwise a routine save.
  const loadTransactions = useCallback((silent = false) => {
    if (!statementId) return Promise.resolve([] as TransactionListItem[]);

    if (!silent) setLoading(true);
    return ManagerStatementService.getTransactions(statementId)
      .then((response: any) => {
        const rows: TransactionListItem[] = response?.transactions ?? [];
        const rowSelectedIds = rows.filter((row) => row.selected).map((row) => row.transaction_id);
        setTransactions(rows);
        setSelectedIds(rowSelectedIds);
        savedSelectedIdsRef.current = rowSelectedIds;
        if (!silent) setLoading(false);
        return rows;
      })
      .catch(() => {
        setTransactions([]);
        setSelectedIds([]);
        savedSelectedIdsRef.current = [];
        if (!silent) setLoading(false);
        return [] as TransactionListItem[];
      });
  }, [statementId]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const handleSelectToggle = useCallback((transactionId: number) => {
    setSelectedIds((prev) => (prev.includes(transactionId) ? prev.filter((id) => id !== transactionId) : [...prev, transactionId]));
  }, []);

  const visibleTransactions = useMemo(
    () => transactions.filter((row) => row.rule_defined),
    [transactions]
  );

  const selectableIds = useMemo(
    () => (readOnly ? [] : visibleTransactions.map((row) => row.transaction_id)),
    [visibleTransactions, readOnly]
  );
  const isAllSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));

  const handleSelectAll = useCallback(() => {
    setSelectedIds((prev) => (
      isAllSelected
        ? prev.filter((id) => !selectableIds.includes(id))
        : [...prev, ...selectableIds.filter((id) => !prev.includes(id))]
    ));
  }, [isAllSelected, selectableIds]);

  const saveSelection = useCallback(() => {
    return ManagerStatementService.saveTransactions(statementId, { transaction_ids: selectedIds })
      .then(() => loadTransactions(true))
      .then((rows) => {
        // A non-empty save may have just linked the first transaction on a statement whose
        // Details tab was never saved - the backend stamps details_saved_at on that first
        // link (see saveTransactions controller action), so refresh the parent statement to
        // reflect the now-unlocked Documents/Payments tabs immediately.
        if (selectedIds.length > 0) {
          onLinked?.();
        }
        return rows;
      });
  }, [statementId, selectedIds, loadTransactions, onLinked]);

  const save = useCallback(() => saveSelection().then(() => undefined), [saveSelection]);

  const isDirty = useCallback(() => {
    const saved = savedSelectedIdsRef.current;
    if (selectedIds.length !== saved.length) return true;
    const savedSet = new Set(saved);
    return selectedIds.some((id) => !savedSet.has(id));
  }, [selectedIds]);

  useImperativeHandle(ref, () => ({ save, isDirty }), [save, isDirty]);

  const selectedTransactions = useMemo(
    () => visibleTransactions.filter((row) => selectedIds.includes(row.transaction_id)),
    [visibleTransactions, selectedIds]
  );

  // Linked (already-saved) rows float to the top, keyed off the server's persisted
  // `selected` flag so a row only jumps up after its save actually completes - not while a
  // checkbox is merely toggled ahead of that save.
  const sortedTransactions = useMemo(() => {
    const sorted = sortRowsByAccessor(visibleTransactions, orderBy, order, TRANSACTIONS_SORT_ACCESSORS);
    return [
      ...sorted.filter((row) => row.selected),
      ...sorted.filter((row) => !row.selected),
    ];
  }, [visibleTransactions, orderBy, order]);

  const totals = useMemo(() => selectedTransactions.reduce((acc, row) => ({
    paidToManager: acc.paidToManager + Number(row.paid_to_manager || 0),
  }), { paidToManager: 0 }), [selectedTransactions]);

  useEffect(() => {
    onTotalsChange?.(totals.paidToManager);
  }, [totals.paidToManager, onTotalsChange]);

  const footerRow = visibleTransactions.length > 0 ? (
    <tr key="footer">
      <td colSpan={7} className="fw-semibold">
        {t('reconciliation.managerStatement.detail.pageTexts.detailsTab.transactions.footer.totalSelected', { count: selectedIds.length })}
      </td>
      <td></td>
      <td></td>
      <td
        className={clsx('fw-semibold', {
          'text-success': totals.paidToManager > 0,
          'text-danger': totals.paidToManager < 0,
        })}
      >
        {formatDollarAmount(formatAmount(totals.paidToManager.toFixed(2)))}
      </td>
      <td colSpan={1}></td>
    </tr>
  ) : undefined;

  const openAdjustModal = useCallback((item: TransactionListItem) => {
    setAdjustFormData({
      paid_to_manager_override: item.paid_to_manager_override !== null ? String(item.paid_to_manager_override) : '',
      manager_note: item.manager_note || '',
      admin_note: item.admin_note || '',
      is_resolved: item.is_resolved,
    });
    setAdjustModal({ open: true, item });
  }, []);

  const handleAdjustClick = useCallback((item: TransactionListItem) => {
    setAttaching(true);
    saveSelection()
      .then((rows) => {
        const updated = rows.find((row) => row.transaction_id === item.transaction_id) ?? item;
        openAdjustModal(updated);
      })
      .finally(() => setAttaching(false));
  }, [saveSelection, openAdjustModal]);

  // Read-only popup, so it opens straight from the row data already in memory - no save or
  // refetch - and keeps the last item mounted while the dialog fades out (same reasoning as
  // handleAdjustClose below).
  const handleCalcClick = useCallback((item: TransactionListItem) => {
    setCalcModal({ open: true, item });
  }, []);

  const handleCalcClose = useCallback(() => {
    setCalcModal((prev) => ({ ...prev, open: false }));
  }, []);

  const handleAdjustClose = useCallback(() => {
    // Keep the last item in place while the dialog fades out (Modal's Fade transition
    // keeps content/action mounted for ~150ms after open becomes false) - nulling it here
    // makes AdjustTransactionModalContent bail out early while the footer buttons keep
    // rendering, flashing a blank dialog with just buttons during the close animation.
    setAdjustModal((prev) => ({ ...prev, open: false }));
    setAdjustFormData(EMPTY_ADJUST_FORM);
  }, []);

  const handleAdjustInputChange = useCallback((e: any, name: string) => {
    const field = ADJUST_FIELD_NAME_MAP[name];
    if (!field) return;

    const value = field === 'is_resolved' ? e.target.checked : e.target.value;
    setAdjustFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleResetToAuto = useCallback(() => {
    setAdjustFormData((prev) => ({ ...prev, paid_to_manager_override: '' }));
  }, []);

  const handleAdjustSave = useCallback(() => {
    if (!adjustModal.item) return;

    setAdjustSaving(true);

    const payload = {
      paid_to_manager_override: adjustFormData.paid_to_manager_override === '' ? null : Number(adjustFormData.paid_to_manager_override),
      manager_note: adjustFormData.manager_note,
      ...(isAdminCaller ? { admin_note: adjustFormData.admin_note, is_resolved: adjustFormData.is_resolved } : {}),
    };

    ManagerStatementService.adjustTransaction(statementId, String(adjustModal.item.transaction_id), payload)
      .then(() => {
        toast.success(
          <Toast message={t('reconciliation.managerStatement.detail.pageTexts.detailsTab.transactions.adjustModal.saveSuccess')} />,
          { icon: false }
        );
        setAdjustSaving(false);
        handleAdjustClose();
        loadTransactions();
      })
      .catch(() => {
        setAdjustSaving(false);
      });
  }, [adjustModal.item, adjustFormData, isAdminCaller, statementId, loadTransactions, handleAdjustClose, t]);

  const tableValues: TableContextProvider = useMemo(() => ({
    rowProps: {
      selectedIds, onSelectToggle: handleSelectToggle, onAdjustClick: handleAdjustClick, onCalcClick: handleCalcClick, readOnly, saving: attaching,
    },
    row: TransactionsRow,
    data: sortedTransactions,
    pagination: false,
    headCells: HEAD_CELLS as HeadCellType[],
    order,
    orderBy,
    page: 1,
    perPage: 9999,
    total: visibleTransactions.length,
    loading,
    noDataFoundMessage: t('reconciliation.managerStatement.detail.pageTexts.detailsTab.transactions.noDataFoundMessage'),
    setPage: () => {},
    setPerPage: () => {},
    setOrder,
    setOrderBy,
    footerRow,
  }), [sortedTransactions, visibleTransactions.length, loading, order, orderBy, selectedIds, handleSelectToggle, handleAdjustClick, handleCalcClick, readOnly, attaching, footerRow, t]);

  const summary = (
    <>
      <i className="ri-shopping-cart-line ri-22px me-2"></i>
      <h6 className="mb-0">
        {t('reconciliation.managerStatement.detail.pageTexts.detailsTab.transactions.title')}
      </h6>
    </>
  );

  const content = loading ? (
    <div className="d-flex justify-content-center p-4">
      <Spinner variant="primary" />
    </div>
  ) : (
    <div>
      {visibleTransactions.length === 0 ? (
        <div className="d-flex justify-content-center p-4">
          <p className="text-muted mb-0">
            {t('reconciliation.managerStatement.detail.pageTexts.detailsTab.transactions.empty')}
          </p>
        </div>
      ) : (
        <div className="row g-6">
          <div className="col-md-12 d-flex align-items-center justify-content-between">
            <div className="row g-3 col-md-2 align-items-center ps-5">
              <div className="form-floating form-floating-outline">
                <Checkbox
                  variant="primary"
                  className="d-checkbox mb-0"
                  name="selectAllTransactions"
                  label={t('reconciliation.managerStatement.detail.pageTexts.detailsTab.transactions.selectAll')}
                  checked={isAllSelected}
                  disabled={selectableIds.length === 0}
                  onChange={handleSelectAll}
                />
              </div>
            </div>
            <div className="row pe-5">
              <div className="d-flex col-md-2 align-items-center ps-5 gap-3">
                <div className="d-flex align-items-center text-nowrap">
                  <i className="ri-circle-fill ri-16px me-1 text-warning"></i>
                  <span>{t('reconciliation.managerStatement.detail.pageTexts.detailsTab.transactions.legend.needsReview')}</span>
                </div>
                <div className="d-flex align-items-center text-nowrap">
                  <i className="ri-circle-fill ri-16px me-1 text-primary"></i>
                  <span>{t('reconciliation.managerStatement.detail.pageTexts.detailsTab.transactions.legend.manuallyAdjusted')}</span>
                </div>
                {readOnly && (
                  <div className="d-flex align-items-center text-nowrap">
                    <i className="ri-circle-fill ri-16px me-1 text-secondary"></i>
                    <span>{t('reconciliation.managerStatement.detail.pageTexts.detailsTab.transactions.legend.locked')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-12">
            <TableBox tableValues={tableValues} />
          </div>
        </div>
      )}
    </div>
  );

  const adjustSubtitle = adjustModal.item
    ? [
        [adjustModal.item.expense_category_label, adjustModal.item.transaction_type_name].filter(Boolean).join(' - '),
        [adjustModal.item.property_name, adjustModal.item.property_name !== managerName ? managerName : null].filter(Boolean).join(' - '),
      ].filter(Boolean).join(' · ')
    : '';

  const adjustModalTitle = (
    <div className="d-flex justify-content-between align-items-start w-100">
      <div>
        <h5 className="fw-bold mb-0">
          {t('reconciliation.managerStatement.detail.pageTexts.detailsTab.transactions.adjustModal.title')}
        </h5>
        {adjustSubtitle && <div className="text-body small fw-semibold">{adjustSubtitle}</div>}
      </div>
      <Button
        className="btn btn-sm btn-icon btn-text-secondary"
        icon={<i className="ri-close-line ri-20px" />}
        onClick={handleAdjustClose}
      />
    </div>
  );

  const calcSubtitle = calcModal.item
    ? [
        [calcModal.item.expense_category_label, calcModal.item.transaction_type_name].filter(Boolean).join(' - '),
        calcModal.item.date,
        calcModal.item.property_name,
      ].filter(Boolean).join(' · ')
    : '';

  const calcModalTitle = (
    <div className="d-flex justify-content-between align-items-start w-100">
      <div>
        <h5 className="fw-bold mb-0">
          {t('reconciliation.managerStatement.detail.pageTexts.detailsTab.transactions.calcModal.title')}
        </h5>
        {calcSubtitle && <div className="text-body small fw-semibold">{calcSubtitle}</div>}
      </div>
      <Button
        className="btn btn-sm btn-icon btn-text-secondary"
        icon={<i className="ri-close-line ri-20px" aria-hidden="true" />}
        aria-label={t('reconciliation.managerStatement.detail.pageTexts.detailsTab.transactions.calcModal.closeBtn')}
        onClick={handleCalcClose}
      />
    </div>
  );

  return (
    <>
      <Accordian
        expanded={expanded}
        onChange={() => setExpanded((prev) => !prev)}
        summary={summary}
        content={content}
        bodyClassName="pb-0 px-0"
      />
      <Modal
        open={calcModal.open}
        onClose={handleCalcClose}
        maxWidth="sm"
        title={calcModalTitle}
        titleClassName="text-start"
        actionClassName="justify-content-end"
        content={<PaidToManagerCalculationModalContent item={calcModal.item} />}
        action={(
          <Button
            className="btn btn-primary"
            text={t('reconciliation.managerStatement.detail.pageTexts.detailsTab.transactions.calcModal.closeBtn')}
            onClick={handleCalcClose}
          />
        )}
      />
      <Modal
        open={adjustModal.open}
        onClose={handleAdjustClose}
        maxWidth="sm"
        title={adjustModalTitle}
        titleClassName="text-start"
        actionClassName="justify-content-start"
        content={(
          <AdjustTransactionModalContent
            item={adjustModal.item}
            formData={adjustFormData}
            isAdminCaller={isAdminCaller}
            readOnly={readOnly}
            onInputChange={handleAdjustInputChange}
            onResetToAuto={handleResetToAuto}
          />
        )}
        action={(
          <AdjustTransactionModalButtons
            loading={adjustSaving}
            readOnly={readOnly}
            onCancel={handleAdjustClose}
            onSave={handleAdjustSave}
          />
        )}
      />
    </>
  );
});

TransactionsSection.displayName = 'TransactionsSection';

export default TransactionsSection;
